import { useNavigate } from 'react-router-dom';

import { CircularProgress, makeStyles } from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiEyeLine,
  RiFileLine,
  RiMore2Fill,
} from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';
import { ButtonIcon, Menu, MenuItem, MenuTrigger, Text } from '@backstage/ui';

import { DialogComposite, useDialog } from '@spotify/confidence-core-react';
import { useCollaborativeDocument } from '@spotify/confidence-editor-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  ArtifactIcon,
  ArtifactListItem,
  DefaultArtifactCard,
  createArtifactExtension,
  getResourceName,
  isExternalLink,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';
import { collaborativeDocumentRouteRef } from '../routes';

const useStyles = makeStyles({
  dialogBody: {
    minHeight: 300,
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 120,
  },
  primaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const reflowSingleLineTables = (markdown: string): string =>
  markdown.replace(/\|(?:[^|\n]*\|)+/g, table => {
    if (table.includes('\n')) return table;
    const separator = table.match(/\|(?::?-+:?\|)+/);
    if (!separator) return table;
    const cols = (separator[0].match(/:?-+:?/g) ?? []).length;
    if (cols === 0) return table;

    const pipesPerRow = cols + 1;
    const rows: string[] = [];
    let rowStart = 0;
    let rowPipes = 0;
    for (let i = 0; i < table.length; i++) {
      if (table[i] !== '|') continue;
      rowPipes++;
      if (rowPipes === pipesPerRow) {
        rows.push(table.slice(rowStart, i + 1).trim());
        rowStart = i + 1;
        rowPipes = 0;
      }
    }
    if (rows.length < 2) return table;
    return `\n\n${rows.join('\n')}\n\n`;
  });

const DocumentArtifactContent = ({
  documentName,
}: {
  documentName: string;
}) => {
  const classes = useStyles();
  const { collaborativeDocument, isLoading } = useCollaborativeDocument({
    name: documentName,
  });

  if (isLoading && !collaborativeDocument) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={24} />
      </div>
    );
  }

  const markdown = collaborativeDocument?.markdown;
  if (!markdown) {
    return (
      <Text variant="body-small" color="secondary">
        This document is empty.
      </Text>
    );
  }

  return <MarkdownContent>{reflowSingleLineTables(markdown)}</MarkdownContent>;
};

const isDocumentArtifact = (artifact: AttachmentFragment) =>
  !isExternalLink(artifact.uri) &&
  getResourceName(artifact.uri).startsWith('collaborativeDocuments/');

const DocumentArtifactListItem = ({
  artifact,
  onSelect,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onSelect: () => void;
  onDelete?: () => void;
}) => {
  const classes = useStyles();
  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <ArtifactListItem.Root onClick={onSelect}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>
          <RiFileLine size={16} />
        </ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" className={classes.primaryText}>
            {artifact.displayName || 'Untitled document'}
          </Text>
        }
        secondary={
          <Text
            variant="body-x-small"
            as="div"
            color="secondary"
            className={classes.secondaryText}
          >
            Document &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <MenuTrigger>
          <ButtonIcon
            variant="tertiary"
            size="small"
            icon={<RiMore2Fill size={16} />}
            aria-label="Document actions"
          />
          <Menu placement="bottom end">
            <MenuItem iconStart={<RiFileLine />} onClick={onSelect}>
              Open document
            </MenuItem>
            {onDelete && (
              <MenuItem
                color="danger"
                iconStart={<RiDeleteBinLine />}
                onClick={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </MenuTrigger>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

const DocumentArtifactHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const navigate = useNavigate();
  const collaborativeDocumentRoute = useRouteRef(collaborativeDocumentRouteRef);
  const documentName = getResourceName(artifact.uri);
  const [, documentId] = documentName.split('/');
  const documentHref = collaborativeDocumentRoute?.({ id: documentId });

  if (!documentHref) {
    return null;
  }

  return (
    <ButtonIcon
      variant="tertiary"
      size="small"
      icon={<RiExternalLinkLine size={16} />}
      onPress={() => navigate(documentHref)}
      aria-label="Pop out document"
    />
  );
};

const DocumentArtifactCard = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const classes = useStyles();
  const { openDialog, closeDialog } = useDialog();

  const handleClick = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.Header>
              <DialogComposite.Title>
                {artifact.displayName || 'Untitled document'}
              </DialogComposite.Title>
              <DialogComposite.CloseButton />
            </DialogComposite.Header>
            <DialogComposite.Body className={classes.dialogBody}>
              <DocumentArtifactContent
                documentName={getResourceName(artifact.uri)}
              />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <DefaultArtifactCard
      artifact={artifact}
      action={<RiEyeLine size={16} />}
      onClick={handleClick}
    />
  );
};

export const DocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'document',
    predicate: isDocumentArtifact,
    icon: () => ({ Icon: RiFileLine, color: '#6366F1' }),
    label: 'Document',
    category: 'Documents',
    render: artifact => (
      <DocumentArtifactContent documentName={getResourceName(artifact.uri)} />
    ),
    renderHeaderActions: artifact => (
      <DocumentArtifactHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete }) => (
      <DocumentArtifactListItem
        artifact={artifact}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    ),
    renderCard: (artifact, _onClick) => (
      <DocumentArtifactCard artifact={artifact} />
    ),
  }),
);
